import { NavLink, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Badge } from '../ui/primitives';
import type { Alert } from '../../lib/types';

const NAV = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/clients', label: 'Clients' },
  { to: '/domains', label: 'Domains' },
  { to: '/contacts', label: 'Contacts' },
  { to: '/campaigns', label: 'Campaigns' },
  { to: '/analytics', label: 'Engagement' },
  { to: '/deliverability', label: 'Deliverability' },
  { to: '/client-questions', label: 'Client answers' },
  { to: '/settings', label: 'Settings' },
];

export function AppShell() {
  const { user, organization, logout } = useAuth();

  const { data: alerts } = useQuery({
    queryKey: ['alerts', 'open'],
    queryFn: () => api.get<{ alerts: Alert[] }>('/api/alerts?status=open'),
    refetchInterval: 60_000,
  });

  const critical = (alerts?.alerts ?? []).filter((a) => a.severity === 'critical').length;

  return (
    <div className="flex min-h-full">
      <aside className="flex w-60 shrink-0 flex-col border-r border-ink-200 bg-white">
        <div className="border-b border-ink-100 px-5 py-5">
          <div className="text-sm font-semibold tracking-tight text-ink-900">EmailBlast</div>
          <div className="mt-0.5 text-xs text-ink-500">{organization?.name ?? 'Deliverability console'}</div>
        </div>

        <nav className="flex-1 space-y-0.5 p-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                'flex items-center justify-between rounded-lg px-3 py-2 text-sm transition ' +
                (isActive ? 'bg-ink-900 text-white' : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900')
              }
            >
              <span>{item.label}</span>
              {item.to === '/deliverability' && critical > 0 && (
                <span className="rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white">
                  {critical}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-ink-100 p-4">
          <div className="text-xs font-medium text-ink-700">{user?.name}</div>
          <div className="mt-0.5 flex items-center justify-between">
            <Badge>{user?.role}</Badge>
            <button onClick={logout} className="text-xs text-ink-500 underline hover:text-ink-900">
              Sign out
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[1400px] p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
